'use client';
import { type ForwardedRef, type JSX } from 'react';

/* eslint-disable-next-line no-restricted-imports */
import RootInputLabel, {
  type InputLabelProps as RootInputLabelProps,
} from '@mui/material/InputLabel';

import { DefaultInputSize, type InputSize } from '../../util/theme/elements/inputs';

export type InputLabelProps = {
  /**
   * The size of the input that the label is associated with.  The label's styling is derived from
   * this size via the 'data-attr-size' attribute that the global theme keys its size-specific
   * styles off of.
   *
   * This replaces MUI's 'size' prop, which only supports 'small' and 'medium', with the
   * application's own {@link InputSize} values so that labels stay consistent with the internalized
   * {@link Select} and {@link FormControl} components.
   *
   * @default DefaultInputSize
   */
  readonly size?: `${InputSize}`;
} & Omit<RootInputLabelProps, 'size'>;

/**
 * An extension of MUI's {@link RootInputLabel} component that constrains the 'size' prop to the
 * application's {@link InputSize} values and communicates the size to the global theme via the
 * 'data-attr-size' attribute rather than MUI's native size mechanism.
 */
export const InputLabel = ({
  ref,
  size = DefaultInputSize,
  ...props
}: { readonly ref?: ForwardedRef<HTMLLabelElement> } & InputLabelProps): JSX.Element => (
  <RootInputLabel {...props} data-attr-size={size} ref={ref} />
);
